import { Octree } from "three/examples/jsm/math/Octree.js";
import { OctreeHelper } from "three/examples/jsm/helpers/OctreeHelper.js";
import { Capsule } from "three/examples/jsm/math/Capsule.js";
import { PerspectiveCamera, Vector3 } from "three";
import * as THREE from "three";

import AppData from "../../app.data";
const appData = new AppData()
const DEFAULT_PLAYER_COLLIDER_HEIGHT = 0.99;

// let instance;

/**
 * Handles physics calculations and collisions for player movement in 3D space
 */
class PhysicsService {
  worldOctree: Octree;              // Spatial partitioning structure for efficient collision detection
  playerCollider: Capsule;          // Capsule-shaped collision body representing the player
  playerVelocity: Vector3;          // Current velocity vector of the player
  playerDirection: Vector3;         // Current direction vector of the player
  playerOnFloor = true;
  mouseTime = 0;
  GRAVITY = 300;
  STEPS_PER_FRAME = 5;
  helper: any;                      // Visual helper for debugging octree structure
  colideSet = false;               // Flag indicating if collision geometry is set up
  navmesh: any;
  private deltaPosition = new Vector3();

  constructor() {
    this.init()
  }

  /**
   * Initializes physics components including player collider and octree structure
   * Creates a capsule collider for the player and sets up the spatial partitioning
   */
  public init() {
    this.worldOctree = new Octree();
    this.playerCollider = new Capsule(
      new Vector3(0, 0.01, 0),     // Bottom point of capsule
      new Vector3(0, 1, 0),        // Top point of capsule
      0.05                         // Radius of capsule
    );
    this.playerVelocity = new Vector3();
    this.playerDirection = new Vector3();
    this.helper = new OctreeHelper(this.worldOctree, 0xff0000);
    this.helper.visible = false;
  }

  /**
   * Cleans up physics resources and resets collision state
   * Called when switching scenes or destroying the physics system
   */
  public dispose() {
    this.resetCollision();
    this.navmesh = null;
  }

  public handleNavMesh(scene: any) {
    this.navmesh = scene.getObjectByName(appData.spaceData.data.navMesh);
    if (this.navmesh) {
        this.navmesh.material.transparent = true;
        this.navmesh.material.depthTest = false;
        this.navmesh.material.depthWrite = false;
        this.navmesh.material.opacity = 0;
        this.setCollision(this.navmesh);

        appData.spaceScene.add(this.helper);
    }
  }

  /**
   * Sets up collision geometry from a mesh
   * Builds octree structure from provided mesh for efficient collision detection
   */
  public setCollision(mesh: any) {
    if (!mesh) return;

    this.resetCollision();
    this.withGalleryCollisionSpace(mesh, () => {
      this.worldOctree.fromGraphNode(mesh);
    });
    this.colideSet = true;
    if (!this.navmesh && this.isConfiguredNavmesh(mesh)) {
      this.navmesh = mesh;
    }
  }

  public movePlayerCollider(deltaPosition: Vector3, maxStep = 0.05) {
    const distance = deltaPosition.length();
    const steps = Math.max(1, Math.ceil(distance / maxStep));
    const step = deltaPosition.clone().divideScalar(steps);

    for (let index = 0; index < steps; index++) {
      this.playerCollider.translate(step);
      this.playerCollisions();
    }

    return this.playerCollider.end.clone();
  }

  public setPlayerColliderHeadPosition(headPosition: Vector3, bodyHeight = DEFAULT_PLAYER_COLLIDER_HEIGHT) {
    const radius = Number.isFinite(this.playerCollider?.radius) ? this.playerCollider.radius : 0;
    const currentHeight = Math.abs(this.playerCollider.end.y - this.playerCollider.start.y);
    const requestedHeight = Number.isFinite(bodyHeight) && bodyHeight > 0
      ? bodyHeight
      : DEFAULT_PLAYER_COLLIDER_HEIGHT;
    const colliderHeight = Math.max(
      currentHeight,
      DEFAULT_PLAYER_COLLIDER_HEIGHT,
      requestedHeight - radius
    );

    this.playerCollider.end.copy(headPosition);
    this.playerCollider.start.copy(headPosition);
    this.playerCollider.start.y = headPosition.y - colliderHeight;
  }

  /**
   * Updates player position based on physics calculations
   * Handles velocity, damping, collision response, and camera position updates
   * @param deltaTime Time step for physics calculation
   */
  private _updateCameraPosOneTime = false;
  public hasActiveMotion() {
    return !this._updateCameraPosOneTime || this.playerVelocity.lengthSq() > 0.000000000001;
  }

  public updatePlayer(deltaTime) {
    // Apply damping to slow down movement over time
    let damping = (Math.exp(-4 * deltaTime) - 1) * 20;
    this.playerVelocity.addScaledVector(this.playerVelocity, damping);

    // Move player based on velocity
    const deltaPosition = this.deltaPosition.copy(this.playerVelocity).multiplyScalar(deltaTime);

    // Only update if movement is significant enough (threshold of 0.001 units)
    if (deltaPosition.length() > 0.00000001 || !this._updateCameraPosOneTime) {
      this.playerCollider.translate(deltaPosition);
      // Check and respond to collisions
      this.playerCollisions();

      // Update camera position to follow player
      appData.camera.position.set(
        this.playerCollider.end.x,
        this.playerCollider.end.y,
        this.playerCollider.end.z
      );
      this._updateCameraPosOneTime = true;
    }
  }

  /**
   * Calculates the forward movement vector based on camera direction
   * Used for player movement in the direction they're facing
   * @returns Normalized vector representing forward direction
   */
  public getForwardVector() {
    appData.camera.getWorldDirection(this.playerDirection);
    this.playerDirection.y = 0;  // Remove vertical component
    this.playerDirection.normalize();
    return this.playerDirection;
  }

  /**
   * Calculates the side movement vector (perpendicular to forward)
   * Used for strafing movement left/right
   * @returns Normalized vector representing side direction
   */
  public getSideVector() {
    appData.camera.getWorldDirection(this.playerDirection);
    this.playerDirection.y = 0;
    this.playerDirection.normalize();
    this.playerDirection.cross(appData.camera.up);
    return this.playerDirection;
  }

  /**
   * Handles collision detection and response
   * Checks for collisions between player capsule and world geometry
   * Adjusts player position when collisions occur
   */
  private playerCollisions() {
    const result = this.worldOctree.capsuleIntersect(this.playerCollider);
    if (result) {
      // Push player out of collision by moving along collision normal
      this.playerCollider.translate(result.normal.multiplyScalar(result.depth));
    }
  }

  private resetCollision() {
    if (typeof this.worldOctree.clear === "function") {
      this.worldOctree.clear();
    } else {
      this.worldOctree.subTrees = [];
      this.worldOctree.box?.makeEmpty();
    }
    this.colideSet = false;
  }

  private withGalleryCollisionSpace(mesh: THREE.Object3D, buildCollision: () => void) {
    const vrRig = appData.renderService?.vrManager?.vrRig as THREE.Group | null | undefined;
    if (!vrRig || !this.isDescendantOf(mesh, vrRig)) {
      buildCollision();
      return;
    }

    const position = vrRig.position.clone();
    const quaternion = vrRig.quaternion.clone();
    const scale = vrRig.scale.clone();

    try {
      vrRig.position.set(0, 0, 0);
      vrRig.quaternion.identity();
      vrRig.scale.set(1, 1, 1);
      vrRig.updateMatrixWorld(true);
      buildCollision();
    } finally {
      vrRig.position.copy(position);
      vrRig.quaternion.copy(quaternion);
      vrRig.scale.copy(scale);
      vrRig.updateMatrixWorld(true);
    }
  }

  private isDescendantOf(object: THREE.Object3D, possibleAncestor: THREE.Object3D) {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (current === possibleAncestor) return true;
      current = current.parent;
    }
    return false;
  }

  private isConfiguredNavmesh(mesh: THREE.Object3D) {
    const navmeshName = appData.spaceData?.data?.navMesh;
    return Boolean(navmeshName && mesh.name === navmeshName);
  }
}

export default PhysicsService;
